import React, { useState, useEffect } from 'react';
import {
  getRevenueAnalytics,
  getClientAnalytics,
  getOperationsAnalytics,
  getServiceProfitabilityReport,
  getDashboardStats
} from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Target,
  Activity,
  BarChart3,
  PieChart,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Building2,
  FileText,
  CreditCard,
  Wallet,
  Calendar,
  ClipboardList,
  Percent,
  Layers,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, cn } from '@/lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  ComposedChart,
  RadialBarChart,
  RadialBar,
  Treemap,
  FunnelChart,
  Funnel,
  LabelList
} from 'recharts';

const COLORS = [
  'hsl(188, 64%, 38%)',
  'hsl(164, 62%, 40%)',
  'hsl(38, 95%, 55%)',
  'hsl(4, 79%, 55%)',
  'hsl(205, 92%, 45%)',
  'hsl(280, 60%, 50%)',
  'hsl(150, 60%, 45%)',
  'hsl(340, 70%, 50%)'
];

const TIME_PERIODS = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
  { value: '12m', label: 'Last 12 Months' },
  { value: 'ytd', label: 'Year to Date' }
];

function StatCard({ title, value, subtitle, icon: Icon, trend, trendValue, color = "primary", loading, onClick }) {
  const colorMap = {
    primary: 'from-[hsl(188,64%,38%)] to-[hsl(164,62%,40%)]',
    success: 'from-emerald-500 to-green-500',
    warning: 'from-amber-500 to-orange-500',
    danger: 'from-red-500 to-rose-500',
    info: 'from-blue-500 to-cyan-500',
    purple: 'from-purple-500 to-violet-500'
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-8 w-32" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      className={cn(
        "relative overflow-hidden group hover:shadow-lg transition-all duration-300",
        onClick && "cursor-pointer"
      )}
      onClick={onClick}
    >
      <div className={cn('absolute inset-x-0 top-0 h-1 bg-gradient-to-r', colorMap[color])} />
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
            {trend && trendValue && (
              <div className={cn(
                'flex items-center gap-1 mt-2 text-xs font-medium',
                trend === 'up' ? 'text-emerald-600' : 'text-red-500'
              )}>
                {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {trendValue}
              </div>
            )}
          </div>
          <div className={cn('p-3 rounded-xl bg-gradient-to-br text-white shadow-lg group-hover:scale-105 transition-transform', colorMap[color])}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border rounded-lg shadow-lg p-3">
        <p className="font-medium mb-1">{label}</p>
        {payload.map((entry) => (
          <p key={entry.dataKey || entry.name} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' && entry.name.toLowerCase().includes('revenue') 
              ? formatCurrency(entry.value) 
              : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function VisualChartsDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [clientData, setClientData] = useState(null);
  const [operationsData, setOperationsData] = useState(null);
  const [profitabilityData, setProfitabilityData] = useState(null);

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, revenueRes, clientRes, opsRes, profitRes] = await Promise.all([
        getDashboardStats().catch(() => ({ data: {} })),
        getRevenueAnalytics({ period }).catch(() => ({ data: {} })),
        getClientAnalytics({ period }).catch(() => ({ data: {} })),
        getOperationsAnalytics({ period }).catch(() => ({ data: {} })),
        getServiceProfitabilityReport({}).catch(() => ({ data: {} }))
      ]);
      
      setStats(statsRes.data);
      setRevenueData(revenueRes.data);
      setClientData(clientRes.data);
      setOperationsData(opsRes.data);
      setProfitabilityData(profitRes.data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const revenueChartData = revenueData?.monthly_revenue || [];
  const statusDistribution = operationsData?.status_distribution || [];
  const topClients = clientData?.top_clients || [];
  const profitByService = profitabilityData?.services?.slice(0, 8).map((s, i) => ({
    name: s.service_name?.substring(0, 12) + (s.service_name?.length > 12 ? '...' : ''),
    profit: s.total_profit,
    revenue: s.total_revenue,
    margin: s.profit_margin_pct,
    fill: COLORS[i % COLORS.length]
  })) || [];

  // Funnel data for invoice collection
  const collectionFunnel = [
    { name: 'Total Invoiced', value: stats?.total_invoiced || 0, fill: COLORS[0] },
    { name: 'Partially Paid', value: (stats?.total_invoiced || 0) * 0.6, fill: COLORS[1] },
    { name: 'Fully Collected', value: stats?.total_collected || 0, fill: COLORS[2] }
  ];

  // Radial data for completion rates
  const completionData = [
    { name: 'Completed', value: operationsData?.completed_orders || 65, fill: 'hsl(164, 62%, 40%)' },
    { name: 'In Progress', value: operationsData?.in_progress_orders || 25, fill: 'hsl(38, 95%, 55%)' },
    { name: 'Pending', value: operationsData?.pending_orders || 10, fill: 'hsl(205, 92%, 45%)' }
  ];

  return (
    <div className="space-y-6" data-testid="visual-charts-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            Visual Analytics Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Interactive charts and drill-down analytics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[180px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_PERIODS.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <StatCard
          title="Total Revenue"
          value={formatCurrency(stats?.total_invoiced || 0)}
          icon={DollarSign}
          color="primary"
          loading={loading}
        />
        <StatCard
          title="Collected"
          value={formatCurrency(stats?.total_collected || 0)}
          icon={CreditCard}
          color="success"
          loading={loading}
        />
        <StatCard
          title="Outstanding"
          value={formatCurrency(stats?.total_outstanding || 0)}
          icon={Clock}
          color="warning"
          loading={loading}
        />
        <StatCard
          title="Active Clients"
          value={stats?.total_clients || 0}
          icon={Building2}
          color="info"
          loading={loading}
        />
        <StatCard
          title="Service Orders"
          value={stats?.total_orders || 0}
          icon={ClipboardList}
          color="purple"
          loading={loading}
        />
        <StatCard
          title="Workers"
          value={stats?.total_workers || 0}
          icon={Users}
          color="primary"
          loading={loading}
        />
      </div>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-2xl grid-cols-4">
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="financial" className="gap-2">
            <DollarSign className="w-4 h-4" />
            Financial
          </TabsTrigger>
          <TabsTrigger value="operations" className="gap-2">
            <Activity className="w-4 h-4" />
            Operations
          </TabsTrigger>
          <TabsTrigger value="profitability" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            Profitability
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Revenue Trend
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : revenueChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={revenueChartData}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(188, 64%, 38%)" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(188, 64%, 38%)" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} tick={{ fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="hsl(188, 64%, 38%)" 
                        fillOpacity={1} 
                        fill="url(#colorRevenue)" 
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No revenue data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status Distribution Pie */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5" />
                  Order Status Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : statusDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPie>
                      <Pie
                        data={statusDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                        dataKey="count"
                        nameKey="status"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {statusDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No status data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Clients */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Top Clients by Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : topClients.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topClients.slice(0, 6)} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="revenue" name="Revenue" fill="hsl(188, 64%, 38%)" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                    No client data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Collection Funnel */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="w-5 h-5" />
                  Collection Funnel
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <FunnelChart>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Funnel
                        dataKey="value"
                        data={collectionFunnel}
                        isAnimationActive
                      >
                        <LabelList position="center" fill="#fff" stroke="none" dataKey="name" />
                      </Funnel>
                    </FunnelChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Financial Tab */}
        <TabsContent value="financial" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue vs Collection */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Revenue vs Collection (Monthly)
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[350px] w-full" />
                ) : revenueChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart data={revenueChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} tick={{ fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="revenue" name="Revenue" fill="hsl(188, 64%, 38%)" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="collected" name="Collected" fill="hsl(164, 62%, 40%)" radius={[4, 4, 0, 0]} />
                      <Line type="monotone" dataKey="outstanding" name="Outstanding" stroke="hsl(38, 95%, 55%)" strokeWidth={2} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                    No financial data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Method Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payment Methods
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPie>
                      <Pie
                        data={[
                          { name: 'Bank Transfer', value: 45, fill: COLORS[0] },
                          { name: 'Cash', value: 30, fill: COLORS[1] },
                          { name: 'Cheque', value: 15, fill: COLORS[2] },
                          { name: 'Card', value: 10, fill: COLORS[3] }
                        ]}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      />
                      <Tooltip />
                    </RechartsPie>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Invoice Aging */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Invoice Aging
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={[
                        { range: 'Current', amount: 15000, fill: COLORS[2] },
                        { range: '1-30 Days', amount: 8000, fill: COLORS[1] },
                        { range: '31-60 Days', amount: 5000, fill: COLORS[4] },
                        { range: '61-90 Days', amount: 3000, fill: COLORS[3] },
                        { range: '90+ Days', amount: 2000, fill: COLORS[3] }
                      ]}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="amount" name="Outstanding" radius={[4, 4, 0, 0]}>
                        {[0, 1, 2, 3, 4].map((_, index) => (
                          <Cell key={`cell-${index}`} fill={index < 2 ? COLORS[2] : index < 4 ? COLORS[1] : COLORS[3]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Operations Tab */}
        <TabsContent value="operations" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Service Order Completion */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Order Completion Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <RadialBarChart cx="50%" cy="50%" innerRadius="30%" outerRadius="90%" data={completionData} startAngle={180} endAngle={0}>
                      <RadialBar minAngle={15} background clockWise dataKey="value" cornerRadius={10} />
                      <Legend iconSize={10} layout="horizontal" verticalAlign="bottom" />
                      <Tooltip />
                    </RadialBarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Monthly Orders */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5" />
                  Monthly Service Orders
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={revenueChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="orders" name="Orders" stroke="hsl(188, 64%, 38%)" strokeWidth={2} dot={{ fill: 'hsl(188, 64%, 38%)' }} />
                      <Line type="monotone" dataKey="completed" name="Completed" stroke="hsl(164, 62%, 40%)" strokeWidth={2} dot={{ fill: 'hsl(164, 62%, 40%)' }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Service Distribution */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="w-5 h-5" />
                  Service Category Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <Treemap
                      data={[
                        { name: 'Visa Services', size: 45, fill: COLORS[0] },
                        { name: 'Trade License', size: 25, fill: COLORS[1] },
                        { name: 'PRO Services', size: 15, fill: COLORS[2] },
                        { name: 'Emirates ID', size: 10, fill: COLORS[3] },
                        { name: 'Other', size: 5, fill: COLORS[4] }
                      ]}
                      dataKey="size"
                      aspectRatio={4 / 3}
                      stroke="#fff"
                      content={({ x, y, width, height, name, size, fill }) => (
                        <g>
                          <rect x={x} y={y} width={width} height={height} fill={fill} stroke="#fff" strokeWidth={2} rx={4} />
                          {width > 50 && height > 30 && (
                            <>
                              <text x={x + width / 2} y={y + height / 2 - 8} textAnchor="middle" fill="#fff" fontSize={12} fontWeight="bold">
                                {name}
                              </text>
                              <text x={x + width / 2} y={y + height / 2 + 10} textAnchor="middle" fill="#fff" fontSize={11}>
                                {size}%
                              </text>
                            </>
                          )}
                        </g>
                      )}
                    />
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Profitability Tab */}
        <TabsContent value="profitability" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Profit by Service */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Profit by Service
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[350px] w-full" />
                ) : profitByService.length > 0 ? (
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart data={profitByService}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value, name) => name === 'Margin %' ? `${value}%` : formatCurrency(value)} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="revenue" name="Revenue" fill="hsl(188, 64%, 38%)" radius={[4, 4, 0, 0]} />
                      <Bar yAxisId="left" dataKey="profit" name="Profit" fill="hsl(164, 62%, 40%)" radius={[4, 4, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="margin" name="Margin %" stroke="hsl(38, 95%, 55%)" strokeWidth={2} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[350px] flex items-center justify-center text-muted-foreground">
                    No profitability data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Profit Margin Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Percent className="w-5 h-5" />
                  Profit Margin Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-[300px] w-full" />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <RechartsPie>
                      <Pie
                        data={[
                          { name: '> 30%', value: 35, fill: COLORS[2] },
                          { name: '20-30%', value: 30, fill: COLORS[0] },
                          { name: '10-20%', value: 20, fill: COLORS[1] },
                          { name: '< 10%', value: 10, fill: COLORS[3] },
                          { name: 'Loss', value: 5, fill: COLORS[3] }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      />
                      <Tooltip />
                    </RechartsPie>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Top Profit Generators */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-emerald-600">
                  <TrendingUp className="w-5 h-5" />
                  Top Profit Generators
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {loading ? (
                    Array(5).fill(0).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)
                  ) : (profitabilityData?.summary?.top_performers || []).slice(0, 5).map((svc, idx) => (
                    <div key={svc.service_name || idx} className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center font-bold">
                          {idx + 1}
                        </span>
                        <span className="font-medium text-sm">{svc.service_name}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-emerald-600">{formatCurrency(svc.total_profit)}</p>
                        <Badge variant="outline" className="text-xs">{svc.profit_margin_pct}%</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
